// Every sending number of every vendor: WhatsApp (WABA) numbers, IVR numbers and SMS sender headers (SPEC §2,
// plan v2 §5.4 / §5.5). The WhatsApp numbers screen owns this file; the IVR, SMS and campaign screens import from it.
// A WhatsApp number comes in by the add-number journey, an IVR number by Sync / Buy / by hand (ivr-numbers.ts); SMS
// headers stay as they are until SMS is built (plan v2 §5.6).
import { z } from 'zod'
import { Channel, CountAnswer, Id, IsoDateTime } from './common'
import type { SyncAnswer } from './ivr-numbers'

/**
 * adding = the WhatsApp add-number journey is not finished (plan v2 §5.5); gone = the vendor's number list no longer
 * has it (Sync); waiting_vendor = the vendor cannot place calls for us yet (Pingbix until its voice API, reminder 74).
 */
export const NumberStatus = z.enum([
  'connected',
  'paused',
  'flagged',
  'blocked',
  'adding',
  'gone',
  'waiting_vendor',
])
export type NumberStatus = z.infer<typeof NumberStatus>

/** Meta's quality rating for a WhatsApp number (IVR and SMS numbers are always high). */
export const Quality = z.enum(['high', 'medium', 'low'])
export type Quality = z.infer<typeof Quality>

/**
 * The kind of an IVR number, read from its digits: promotional calls by an RBI-regulated company only from
 * 140-series numbers, service calls from 1600-series (plan v2 §1, §10); anything else is a normal number.
 */
export const NumberSeries = z.enum(['promo_140', 'service_1600', 'normal'])
export type NumberSeries = z.infer<typeof NumberSeries>

/** Where the portal got the number from. */
export const NumberSource = z.enum(['vendor_sync', 'bought', 'by_hand', 'journey'])
export type NumberSource = z.infer<typeof NumberSource>

/** The WhatsApp add-number journey (plan v2 §5.5): 1 number + name → 2 finish it in Exotel → 3 check Meta. */
export const WhatsAppSetup = z.object({
  display_name: z.string().describe('the name people see in WhatsApp; Meta reviews it'),
  asked_at: IsoDateTime.describe('when step 1 was saved'),
  request_text: z.string().describe('the text for Exotel (dashboard or a support request); no personal data'),
  meta_status: z.enum(['not_found', 'pending', 'connected']).describe("Meta's answer at the last check"),
  checked_at: IsoDateTime.nullable(),
  checks: z.number().int().min(0),
})
export type WhatsAppSetup = z.infer<typeof WhatsAppSetup>

export const PhoneNumber = z.object({
  id: Id,
  channel: Channel,
  display: z.string().describe('the number or the DLT header, e.g. +91 140 447 1001 or FFINCP'),
  name: z.string(),
  vendor_id: Id,
  vendor_name: z.string(),
  status: NumberStatus,
  quality: Quality,
  daily_limit: z.number().int().min(1).describe('Meta tier for WhatsApp, calls per day for IVR'),
  tier: z.string(),
  sent_today: z.number().int().min(0),
  sent_total: z.number().int().min(0),
  blocks: z.number().int().min(0).describe('blocks and spam reports counted against the number'),
  note: z.string(),
  series: NumberSeries.nullable().describe('IVR only: 140 promotional / 1600 service / normal'),
  region: z.string().describe('IVR: the telecom circle, e.g. Karnataka; empty for the other channels'),
  source: NumberSource,
  setup: WhatsAppSetup.nullable().describe('WhatsApp only, while the add-number journey runs'),
  waba_id: z
    .string()
    .nullable()
    .optional()
    .describe('WT1: the WhatsApp account (WABA) a WhatsApp number is on; null / missing = none picked (counts on the first)'),
})
export type PhoneNumber = z.infer<typeof PhoneNumber>

export const NumberFilter = z.object({
  channel: Channel.optional(),
  vendor_id: Id.optional(),
  series: NumberSeries.optional(),
  status: NumberStatus.optional(),
})
export type NumberFilter = z.infer<typeof NumberFilter>

/** An Indian mobile for WhatsApp: 10 digits from 6 to 9, with or without +91 and spaces. */
const IndianMobile = z
  .string()
  .trim()
  .refine((value) => /^[6-9]\d{9}$/.test(value.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '')), {
    message: 'A 10-digit Indian mobile, e.g. +91 98 7000 0011',
  })

/** A WABA number already on Meta may be a landline too (e.g. +91 22 4178 0723): 10 digits, not from 0. */
const IndianNumber = z
  .string()
  .trim()
  .refine((value) => /^[1-9]\d{9}$/.test(value.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '')), {
    message: 'A 10-digit Indian number, e.g. +91 22 4178 0723',
  })

export const StartWhatsAppNumberInput = z.object({
  display: IndianMobile,
  display_name: z
    .string()
    .trim()
    .min(3, 'At least 3 letters')
    .max(40, 'At most 40 letters')
    .describe('Meta: it must match the business, e.g. "First Fund Fincorp"'),
})
export type StartWhatsAppNumberInput = z.infer<typeof StartWhatsAppNumberInput>

/** Meta's messaging limits (business-initiated conversations a day); the highest also stands for unlimited. */
export const META_LIMITS = [250, 1000, 10000, 100000] as const

/**
 * Reminder 103: a number already connected on our WABA, added by hand (Exotel has no API that lists our WhatsApp
 * numbers, so Import cannot bring it). Quality and limit as WhatsApp Manager shows them.
 */
export const AddWhatsAppNumberInput = z.object({
  display: IndianNumber,
  name: z.string().trim().min(1, 'Give it a name').max(40, 'At most 40 letters'),
  quality: Quality,
  daily_limit: z.number().int().min(1).max(100000),
  waba_id: z.string().optional().describe('W1: the WhatsApp account it is on; missing = the first one'),
})
export type AddWhatsAppNumberInput = z.infer<typeof AddWhatsAppNumberInput>

/**
 * Owner 2026-11-03 ("go finish by hand"): step 3 of the add-number journey when Check again cannot see the number
 * (Exotel lists no WhatsApp numbers, reminder 103): it is connected in WhatsApp Manager, so it is marked connected by
 * hand, with the quality and limit WhatsApp Manager shows.
 */
export const FinishWhatsAppNumberInput = z.object({
  quality: Quality,
  daily_limit: z.number().int().min(1).max(100000),
})
export type FinishWhatsAppNumberInput = z.infer<typeof FinishWhatsAppNumberInput>

/** W1: Meta's tiers for a WABA (business-initiated conversations in a rolling 24 h); null = unlimited. */
export const WABA_LIMITS = [250, 2000, 10000, 100000] as const

export const WhatsAppAccountStatus = z.enum(['active', 'paused'])
export type WhatsAppAccountStatus = z.infer<typeof WhatsAppAccountStatus>

/** Meta's WABA ID: digits only, e.g. 1099007866328894 (WhatsApp Manager → the account's ID). */
const WabaId = z
  .string()
  .trim()
  .regex(/^[0-9]{6,20}$/, 'The WABA ID: digits only, as WhatsApp Manager shows it')

/**
 * W1 (owner 2026-11-03): a WhatsApp Business account (WABA), entered by hand (Exotel has no number API, the Meta app
 * is not set up yet). Meta's limit belongs to it and is shared by all its numbers; when it shares Meta's limit with
 * another WABA (W-Q1: Meta may count a business portfolio together) the counts below are the shared ones.
 */
export const WhatsAppAccount = z.object({
  id: z.string().describe("Meta's WABA ID"),
  name: z.string(),
  vendor_id: Id,
  vendor_name: z.string(),
  status: WhatsAppAccountStatus.describe('paused: none of its numbers sends'),
  daily_limit: z.number().int().min(1).nullable().describe("Meta's limit, a rolling 24 h; null = unlimited"),
  shares_limit_with: z.string().nullable().describe('another WABA whose limit this one uses too'),
  note: z.string(),
  used_24h: z.number().int().min(0).describe('WhatsApp sent by the portal in the last 24 h (its numbers, or the shared ones)'),
  outside_24h: z.number().int().min(0).describe("W3: sent outside the portal (Exotel's own page), seen by webhook"),
  stop_at: z.number().int().min(0).nullable().describe('the portal stops here (Settings: stop share); null = no stop'),
  left: z.number().int().min(0).nullable().describe('stop_at − both counts, never below 0; null = no stop'),
  numbers: z.array(
    z.object({
      id: Id,
      display: z.string(),
      name: z.string(),
      status: NumberStatus,
      quality: Quality,
      sent_today: z.number().int().min(0),
    })
  ),
  updated_at: IsoDateTime,
  updated_by_name: z.string().nullable(),
})
export type WhatsAppAccount = z.infer<typeof WhatsAppAccount>

export const WhatsAppAccountList = z.object({
  accounts: z.array(WhatsAppAccount),
  unlinked: z
    .array(z.object({ id: Id, display: z.string(), name: z.string() }))
    .describe('WhatsApp numbers on no account: they send on the first account until one is picked'),
  stop_percent: z.number().int().min(1).max(100).describe('Settings: the share of the limit the portal stops at'),
})
export type WhatsAppAccountList = z.infer<typeof WhatsAppAccountList>

export const EditWhatsAppAccountInput = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(60, 'At most 60 letters'),
  daily_limit: z.number().int().min(1).max(1000000).nullable(),
  shares_limit_with: z.string().nullable(),
  status: WhatsAppAccountStatus,
  note: z.string().trim().max(200, 'At most 200 letters'),
})
export type EditWhatsAppAccountInput = z.infer<typeof EditWhatsAppAccountInput>

export const AddWhatsAppAccountInput = EditWhatsAppAccountInput.extend({ id: WabaId })
export type AddWhatsAppAccountInput = z.infer<typeof AddWhatsAppAccountInput>

export const SetNumberAccountInput = z.object({ waba_id: z.string().min(1, 'Pick a WhatsApp account') })
export type SetNumberAccountInput = z.infer<typeof SetNumberAccountInput>

export interface NumbersApi {
  listNumbers(filter?: NumberFilter): Promise<PhoneNumber[]>
  pauseNumber(id: string): Promise<PhoneNumber>
  resumeNumber(id: string): Promise<PhoneNumber>
  /** after Meta lifts a flag: back to connected, quality medium, blocks 0 */
  markNumberCleared(id: string): Promise<PhoneNumber>
  /** asks the vendor / Meta for every number's status; count = numbers that changed */
  refreshNumberStatus(): Promise<CountAnswer>
  /**
   * CW1: reads every WhatsApp number of our WABA from Exotel; one connected at Meta and not in the list yet comes in
   * (source vendor_sync, with its quality and limit). added = new here; unchanged = listed already or not connected
   * yet; gone is always 0 (a number is never taken out by an import).
   */
  importWhatsAppNumbers(): Promise<SyncAnswer>
  /** step 1 of the add-number journey: the number waits as "adding" with the text for Exotel */
  startWhatsAppNumber(input: StartWhatsAppNumberInput): Promise<PhoneNumber>
  /** reminder 103: a number already on our WABA comes in connected (source by_hand), quality and limit as typed */
  addWhatsAppNumber(input: AddWhatsAppNumberInput): Promise<PhoneNumber>
  /** step 3: reads the number from Meta (our WABA); connected once Meta has it, with quality and limit */
  checkWhatsAppNumber(id: string): Promise<PhoneNumber>
  /** step 3 by hand: a number being added is connected in WhatsApp Manager (quality and limit as shown there) */
  finishWhatsAppNumber(id: string, input: FinishWhatsAppNumberInput): Promise<PhoneNumber>
  /** W1: our WhatsApp accounts (WABA) with Meta's limit and its use, and the numbers on none */
  listWhatsAppAccounts(): Promise<WhatsAppAccountList>
  addWhatsAppAccount(input: AddWhatsAppAccountInput): Promise<WhatsAppAccount>
  editWhatsAppAccount(id: string, input: EditWhatsAppAccountInput): Promise<WhatsAppAccount>
  /** W1: moves a WhatsApp number onto an account */
  setNumberAccount(numberId: string, input: SetNumberAccountInput): Promise<PhoneNumber>
}
